/**
 * Package Assignment Management Page
 * Allows manufacturers to assign packages to dealers and track their status
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { packageAssignmentService, PackageAssignment, PackageAssignmentData } from '../services/package-assignment.service';
import { dealerService, Dealer } from '../services/dealer.service';
import { Button, Input, Alert, Modal, FilterBar, PageHeader, StatusBadge } from '../components/ui';
import { mapStatusToBadge } from '../lib/status';

export default function PackageAssignmentPage() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<PackageAssignment[]>([]);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState<PackageAssignmentData>({
    dealerId: '',
    packageId: '',
    qrToken: '',
    packageType: 'unit',
    packageDetails: {
      contents: '',
      quantity: 1,
      description: '',
      value: 0,
    },
    expiresInHours: 48,
  });

  // Edit assignment modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<PackageAssignment | null>(null);
  const [updating, setUpdating] = useState(false);

  // Edit dealer modal state
  const [showEditDealerModal, setShowEditDealerModal] = useState(false);
  const [editingDealer, setEditingDealer] = useState<Dealer | null>(null);
  const [updatingDealer, setUpdatingDealer] = useState(false);

  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printingAssignment, setPrintingAssignment] = useState<PackageAssignment | null>(null);

  // Dealer list modal state
  const [showDealerListModal, setShowDealerListModal] = useState(false);

  // Filter state
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDealer, setFilterDealer] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'assigned-desc' | 'assigned-asc' | 'expires-asc' | 'expires-desc'>(
    'assigned-desc'
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load assignments and dealers in parallel
      const [assignmentsResult, dealersResult] = await Promise.all([
        packageAssignmentService.getPackageAssignments(),
        dealerService.getDealerNetwork()
      ]);

      if (assignmentsResult.success && assignmentsResult.assignments) {
        setAssignments(assignmentsResult.assignments);
      } else {
        setError(assignmentsResult.error || 'Failed to load assignments');
      }

      if (dealersResult.success && dealersResult.dealers) {
        setDealers(dealersResult.dealers);
      } else {
        console.warn('Failed to load dealers:', dealersResult.error);
      }
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePackages = async (packageType: string) => {
    try {
      setLoadingPackages(true);
      setError(null);
      
      const result = await packageAssignmentService.getAvailablePackages(packageType);
      
      if (result.success && result.packages) {
        setAvailablePackages(result.packages);
      } else {
        setError(result.error || 'Failed to load available packages');
        setAvailablePackages([]);
      }
    } catch (err: any) {
      console.error('Failed to load available packages:', err);
      setError('Failed to load available packages');
      setAvailablePackages([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleAssignPackage = async () => {
    try {
      setAssigning(true);
      setError(null);

      // Validate form
      if (!assignmentForm.dealerId || !assignmentForm.packageId || !assignmentForm.packageDetails.contents) {
        setError('Please fill in all required fields');
        return;
      }

      // Generate QR token if not provided
      if (!assignmentForm.qrToken) {
        assignmentForm.qrToken = `QR_${assignmentForm.packageId}_${Date.now()}`;
      }

      const result = await packageAssignmentService.assignPackage(assignmentForm);
      
      if (result.success) {
        setSuccess(`Package ${assignmentForm.packageId} assigned successfully!`);
        setShowAssignModal(false);
        resetAssignmentForm();
        loadData(); // Reload assignments
      } else {
        setError(result.error || 'Failed to assign package');
      }
    } catch (err: any) {
      console.error('Failed to assign package:', err);
      setError('Failed to assign package');
    } finally {
      setAssigning(false);
    }
  };

  const resetAssignmentForm = () => {
    setAssignmentForm({
      dealerId: '',
      packageId: '',
      qrToken: '',
      packageType: 'unit',
      packageDetails: {
        contents: '',
        quantity: 1,
        description: '',
        value: 0,
      },
      expiresInHours: 48,
    });
    setAvailablePackages([]);
  };

  const handlePackageTypeChange = (packageType: string) => {
    setAssignmentForm({ 
      ...assignmentForm, 
      packageType: packageType as 'pallet' | 'case' | 'unit',
      packageId: '', // Reset package ID when type changes
      qrToken: '',
      packageDetails: {
        ...assignmentForm.packageDetails,
        contents: '',
      }
    });
    
    // Load available packages for the selected type
    if (packageType !== 'unit') {
      loadAvailablePackages(packageType);
    } else {
      setAvailablePackages([]);
    }
  };

  const handlePackageSelection = (packageId: string) => {
    const selectedPackage = availablePackages.find(pkg => pkg.id === packageId);
    if (selectedPackage) {
      setAssignmentForm({
        ...assignmentForm,
        packageId: selectedPackage.id,
        qrToken: selectedPackage.qrToken,
        packageDetails: {
          ...assignmentForm.packageDetails,
          contents: `${selectedPackage.packageType} - ${selectedPackage.batch?.productName || 'Package'}`,
          quantity: selectedPackage.totalUnits || 1,
        }
      });
    }
  };

  const handleEditAssignment = (assignment: PackageAssignment) => {
    setEditingAssignment(assignment);
    setShowEditModal(true);
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await packageAssignmentService.deleteAssignment(assignmentId);
      
      if (result.success) {
        setSuccess('Assignment deleted successfully!');
        loadData(); // Reload assignments
      } else {
        setError(result.error || 'Failed to delete assignment');
      }
    } catch (err: any) {
      console.error('Failed to delete assignment:', err);
      setError('Failed to delete assignment');
    }
  };

  const handleUpdateAssignment = async () => {
    if (!editingAssignment) return;

    try {
      setUpdating(true);
      setError(null);

      const result = await packageAssignmentService.updateAssignment(editingAssignment.id, {
        packageDetails: editingAssignment.packageDetails,
        expiresInHours: Math.ceil((new Date(editingAssignment.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60))
      });
      
      if (result.success) {
        setSuccess('Assignment updated successfully!');
        setShowEditModal(false);
        setEditingAssignment(null);
        loadData(); // Reload assignments
      } else {
        setError(result.error || 'Failed to update assignment');
      }
    } catch (err: any) {
      console.error('Failed to update assignment:', err);
      setError('Failed to update assignment');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditDealer = (dealer: Dealer) => {
    setEditingDealer(dealer);
    setShowEditDealerModal(true);
  };

  const handleUpdateDealer = async () => {
    if (!editingDealer) return;

    try {
      setUpdatingDealer(true);
      setError(null);

      const result = await dealerService.updateDealer(editingDealer.id, {
        name: editingDealer.name,
        mobileNumber: editingDealer.mobileNumber,
        address: editingDealer.address,
        email: editingDealer.email,
        businessName: editingDealer.businessName,
        gstNumber: editingDealer.gstNumber,
        contactPerson: editingDealer.contactPerson,
      });
      
      if (result.success) {
        setSuccess('Dealer updated successfully!');
        setShowEditDealerModal(false);
        setEditingDealer(null);
        loadData(); // Reload data
      } else {
        setError(result.error || 'Failed to update dealer');
      }
    } catch (err: any) {
      console.error('Failed to update dealer:', err);
      setError('Failed to update dealer');
    } finally {
      setUpdatingDealer(false);
    }
  };

  const handlePrintLabel = (assignment: PackageAssignment) => {
    setPrintingAssignment(assignment);
    setShowPrintModal(true);
  };

  const handlePrintDestinationLabel = async () => {
    if (!printingAssignment) return;

    try {
      const result = await packageAssignmentService.generateDestinationLabel(printingAssignment.id);
      
      if (result.success && result.pdfUrl) {
        // Open PDF in new window for printing
        window.open(result.pdfUrl, '_blank');
        setSuccess('Destination label generated successfully!');
        setShowPrintModal(false);
        setPrintingAssignment(null);
      } else {
        setError(result.error || 'Failed to generate destination label');
      }
    } catch (err: any) {
      console.error('Failed to generate destination label:', err);
      setError('Failed to generate destination label');
    }
  };

  const handleSendNotification = async (assignmentId: string) => {
    try {
      const result = await packageAssignmentService.sendAssignmentNotification(assignmentId);
      
      if (result.success) {
        setSuccess('Notification sent successfully!');
        loadData(); // Reload to update status
      } else {
        setError(result.error || 'Failed to send notification');
      }
    } catch (err: any) {
      console.error('Failed to send notification:', err);
      setError('Failed to send notification');
    }
  };

  const getPackageTypeTone = (type: string) => {
    switch (type) {
      case 'pallet':
        return 'anchored';
      case 'case':
        return 'active';
      case 'unit':
      default:
        return 'muted';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: assignments.length },
      { value: 'assigned', label: 'Assigned', count: assignments.filter((a) => a.status === 'assigned').length },
      { value: 'notified', label: 'Notified', count: assignments.filter((a) => a.status === 'notified').length },
      { value: 'accepted', label: 'Accepted', count: assignments.filter((a) => a.status === 'accepted').length },
      { value: 'expired', label: 'Expired', count: assignments.filter((a) => a.status === 'expired').length },
    ],
    [assignments]
  );

  const filteredAssignments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = assignments.filter((assignment) => {
      const matchesStatus = filterStatus === 'all' || assignment.status === filterStatus;
      const matchesDealer = filterDealer === 'all' || assignment.dealer.id === filterDealer;
      const matchesSearch =
        normalizedSearch === '' ||
        assignment.packageId.toLowerCase().includes(normalizedSearch) ||
        assignment.dealer.name.toLowerCase().includes(normalizedSearch) ||
        assignment.packageDetails.contents.toLowerCase().includes(normalizedSearch);

      return matchesStatus && matchesDealer && matchesSearch;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'assigned-asc') {
        return new Date(a.assignedAt).getTime() - new Date(b.assignedAt).getTime();
      }
      if (sortBy === 'expires-asc') {
        return new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime();
      }
      if (sortBy === 'expires-desc') {
        return new Date(b.expiresAt).getTime() - new Date(a.expiresAt).getTime();
      }
      return new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime();
    });
  }, [assignments, filterDealer, filterStatus, searchTerm, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading package assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <PageHeader
            title="Package Assignments"
            description="Assign packages to dealers and track their acceptance status."
            actions={
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" onClick={() => navigate('/manufacturer')}>
                  Back to Dashboard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowDealerListModal(true)}
                  className="flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  Manage Dealers
                </Button>
                <Button
                  onClick={() => setShowAssignModal(true)}
                  className="flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Assign Package
                </Button>
              </div>
            }
          />
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}
        {/* Filters */}
        <FilterBar
          className="mb-4"
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by package ID, dealer name, or contents..."
          statusOptions={statusOptions}
          selectedStatus={filterStatus}
          onStatusChange={setFilterStatus}
          sortOptions={[
            { value: 'assigned-desc', label: 'Assigned (Newest)' },
            { value: 'assigned-asc', label: 'Assigned (Oldest)' },
            { value: 'expires-asc', label: 'Expires (Soonest)' },
            { value: 'expires-desc', label: 'Expires (Latest)' },
          ]}
          selectedSort={sortBy}
          onSortChange={(value) =>
            setSortBy(value as 'assigned-desc' | 'assigned-asc' | 'expires-asc' | 'expires-desc')
          }
        />
        <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="text-sm font-medium text-gray-700 sm:min-w-40">Dealer filter</label>
            <div className="flex flex-1 items-center gap-2">
              <select
                value={filterDealer}
                onChange={(event) => setFilterDealer(event.target.value)}
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              >
                <option value="all">All dealers</option>
                {dealers.map((dealer) => (
                  <option key={dealer.id} value={dealer.id}>
                    {dealer.name}
                  </option>
                ))}
              </select>
              {filterDealer !== 'all' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const dealer = dealers.find((item) => item.id === filterDealer);
                    if (dealer) {
                      handleEditDealer(dealer);
                    }
                  }}
                >
                  Edit Dealer
                </Button>
              )}
            </div>
          </div>
        </section>
        {/* Assignments List */}
        {filteredAssignments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Package Assignments Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterStatus !== 'all' || filterDealer !== 'all'
                ? 'No assignments match your current filters.' 
                : 'Start by assigning packages to your dealers.'
              }
            </p>
            <Button
              onClick={() => setShowAssignModal(true)}
              className="mt-4"
            >
              Assign First Package
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Package Assignments ({filteredAssignments.length})
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Package assignments">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Package
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dealer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expires
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAssignments.map((assignment) => (
                    <tr key={assignment.id} className="hover:bg-gray-50 group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{assignment.packageId}</div>
                          <div className="text-sm text-gray-500">{assignment.packageDetails.contents}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <StatusBadge
                              size="sm"
                              tone={getPackageTypeTone(assignment.packageType)}
                              label={assignment.packageType}
                            />
                            <span className="text-xs text-gray-500">
                              Qty: {assignment.packageDetails.quantity}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{assignment.dealer.name}</div>
                            <div className="text-sm text-gray-500">{assignment.dealer.mobileNumber}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const dealer = dealers.find(d => d.id === assignment.dealer.id);
                              if (dealer) handleEditDealer(dealer);
                            }}
                            className="text-blue-600 hover:text-blue-900 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit Dealer"
                            aria-label={`Edit dealer ${assignment.dealer.name}`}
                          >
                            ✏️
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge {...mapStatusToBadge(assignment.status)} size="sm" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(assignment.assignedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(assignment.expiresAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {assignment.status === 'assigned' && (
                            <button
                              onClick={() => handleSendNotification(assignment.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Send SMS"
                            >
                              📱
                            </button>
                          )}
                          <button
                            onClick={() => handlePrintLabel(assignment)}
                            className="text-green-600 hover:text-green-900"
                            title="Print Destination Label"
                          >
                            🖨️
                          </button>
                          <button
                            onClick={() => handleEditAssignment(assignment)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Edit Assignment"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteAssignment(assignment.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Assignment"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      {/* Assign Package Modal */}
      {showAssignModal && (
        <Modal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            resetAssignmentForm();
            setError(null);
          }}
          title="Assign Package to Dealer"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Package Type *
                </label>
                <select
                  value={assignmentForm.packageType}
                  onChange={(e) => handlePackageTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                >
                  <option value="unit">Unit (Manual Entry)</option>
                  <option value="case">Case</option>
                  <option value="pallet">Pallet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Package ID *
                </label>
                {assignmentForm.packageType === 'unit' ? (
                  <Input
                    value={assignmentForm.packageId}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, packageId: e.target.value })}
                    placeholder="Enter package ID manually"
                    className="text-gray-900 bg-white"
                  />
                ) : (
                  <select
                    value={assignmentForm.packageId}
                    onChange={(e) => handlePackageSelection(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                    disabled={loadingPackages}
                  >
                    <option value="">
                      {loadingPackages ? 'Loading packages...' : 'Select a package'}
                    </option>
                    {availablePackages.map(pkg => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.id.substring(0, 8)}... - {pkg.batch?.productName || 'Package'} ({pkg.totalUnits || 0} units)
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dealer *
              </label>
              <select
                value={assignmentForm.dealerId}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, dealerId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              >
                <option value="">Select a dealer</option>
                {dealers.map(dealer => (
                  <option key={dealer.id} value={dealer.id}>
                    {dealer.name} ({dealer.mobileNumber})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Contents *
              </label>
              <Input
                value={assignmentForm.packageDetails.contents}
                onChange={(e) => setAssignmentForm({ 
                  ...assignmentForm, 
                  packageDetails: { ...assignmentForm.packageDetails, contents: e.target.value }
                })}
                placeholder="e.g., Ghee bottles - 500ml x 24 units"
                className="text-gray-900 bg-white"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity *
                </label>
                <Input
                  type="number"
                  value={assignmentForm.packageDetails.quantity}
                  onChange={(e) => setAssignmentForm({ 
                    ...assignmentForm, 
                    packageDetails: { ...assignmentForm.packageDetails, quantity: parseInt(e.target.value) || 1 }
                  })}
                  min="1"
                  className="text-gray-900 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Value (₹)
                </label>
                <Input
                  type="number"
                  value={assignmentForm.packageDetails.value}
                  onChange={(e) => setAssignmentForm({ 
                    ...assignmentForm, 
                    packageDetails: { ...assignmentForm.packageDetails, value: parseFloat(e.target.value) || 0 }
                  })}
                  min="0"
                  step="0.01"
                  className="text-gray-900 bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <Input
                value={assignmentForm.packageDetails.description}
                onChange={(e) => setAssignmentForm({ 
                  ...assignmentForm, 
                  packageDetails: { ...assignmentForm.packageDetails, description: e.target.value }
                })}
                placeholder="Additional description (optional)"
                className="text-gray-900 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expires In (Hours)
              </label>
              <select
                value={assignmentForm.expiresInHours}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, expiresInHours: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              >
                <option value={24}>24 hours</option>
                <option value={48}>48 hours (default)</option>
                <option value={72}>72 hours</option>
                <option value={168}>1 week</option>
              </select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                The dealer will receive an SMS notification with package details. They can scan the QR code and use OTP verification to accept the package.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAssignModal(false);
                  resetAssignmentForm();
                  setError(null);
                }}
                disabled={assigning}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignPackage}
                disabled={assigning}
              >
                {assigning ? 'Assigning Package...' : 'Assign Package'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Assignment Modal */}
      {showEditModal && editingAssignment && (
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingAssignment(null);
            setError(null);
          }}
          title="Edit Package Assignment"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package ID
              </label>
              <Input
                value={editingAssignment.packageId}
                disabled
                className="text-gray-900 bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dealer
              </label>
              <Input
                value={editingAssignment.dealer.name}
                disabled
                className="text-gray-900 bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Contents *
              </label>
              <Input
                value={editingAssignment.packageDetails.contents}
                onChange={(e) => setEditingAssignment({
                  ...editingAssignment,
                  packageDetails: { ...editingAssignment.packageDetails, contents: e.target.value }
                })}
                className="text-gray-900 bg-white"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity *
                </label>
                <Input
                  type="number"
                  value={editingAssignment.packageDetails.quantity}
                  onChange={(e) => setEditingAssignment({
                    ...editingAssignment,
                    packageDetails: { ...editingAssignment.packageDetails, quantity: parseInt(e.target.value) || 1 }
                  })}
                  min="1"
                  className="text-gray-900 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Value (₹)
                </label>
                <Input
                  type="number"
                  value={editingAssignment.packageDetails.value}
                  onChange={(e) => setEditingAssignment({
                    ...editingAssignment,
                    packageDetails: { ...editingAssignment.packageDetails, value: parseFloat(e.target.value) || 0 }
                  })}
                  min="0"
                  step="0.01"
                  className="text-gray-900 bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <Input
                value={editingAssignment.packageDetails.description || ''}
                onChange={(e) => setEditingAssignment({
                  ...editingAssignment,
                  packageDetails: { ...editingAssignment.packageDetails, description: e.target.value }
                })}
                placeholder="Additional description (optional)"
                className="text-gray-900 bg-white"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAssignment(null);
                  setError(null);
                }}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateAssignment}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Update Assignment'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Dealer Modal */}
      {showEditDealerModal && editingDealer && (
        <Modal
          isOpen={showEditDealerModal}
          onClose={() => {
            setShowEditDealerModal(false);
            setEditingDealer(null);
            setError(null);
          }}
          title="Edit Dealer Information"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dealer Name *
                </label>
                <Input
                  value={editingDealer.name}
                  onChange={(e) => setEditingDealer({ ...editingDealer, name: e.target.value })}
                  className="text-gray-900 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name
                </label>
                <Input
                  value={editingDealer.businessName || ''}
                  onChange={(e) => setEditingDealer({ ...editingDealer, businessName: e.target.value })}
                  className="text-gray-900 bg-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <Input
                  value={editingDealer.mobileNumber}
                  onChange={(e) => setEditingDealer({ ...editingDealer, mobileNumber: e.target.value })}
                  className="text-gray-900 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  value={editingDealer.email || ''}
                  onChange={(e) => setEditingDealer({ ...editingDealer, email: e.target.value })}
                  className="text-gray-900 bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address *
              </label>
              <Input
                value={editingDealer.address}
                onChange={(e) => setEditingDealer({ ...editingDealer, address: e.target.value })}
                className="text-gray-900 bg-white"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GST Number
                </label>
                <Input
                  value={editingDealer.gstNumber || ''}
                  onChange={(e) => setEditingDealer({ ...editingDealer, gstNumber: e.target.value })}
                  className="text-gray-900 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person
                </label>
                <Input
                  value={editingDealer.contactPerson || ''}
                  onChange={(e) => setEditingDealer({ ...editingDealer, contactPerson: e.target.value })}
                  className="text-gray-900 bg-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDealerModal(false);
                  setEditingDealer(null);
                  setError(null);
                }}
                disabled={updatingDealer}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateDealer}
                disabled={updatingDealer}
              >
                {updatingDealer ? 'Updating...' : 'Update Dealer'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Print Destination Label Modal */}
      {showPrintModal && printingAssignment && (
        <Modal
          isOpen={showPrintModal}
          onClose={() => {
            setShowPrintModal(false);
            setPrintingAssignment(null);
          }}
          title="Print Destination Label"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-blue-900 mb-2">Package Details</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p><strong>Package ID:</strong> {printingAssignment.packageId}</p>
                <p><strong>Type:</strong> {printingAssignment.packageType}</p>
                <p><strong>Contents:</strong> {printingAssignment.packageDetails.contents}</p>
                <p><strong>Quantity:</strong> {printingAssignment.packageDetails.quantity}</p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-green-900 mb-2">Destination</h3>
              <div className="space-y-2 text-sm text-green-800">
                <p><strong>Dealer:</strong> {printingAssignment.dealer.name}</p>
                <p><strong>Mobile:</strong> {printingAssignment.dealer.mobileNumber}</p>
                <p><strong>Address:</strong> Will be included in the label</p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                📋 This will generate a PDF label with package details and dealer address that can be printed and attached to the package.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPrintModal(false);
                  setPrintingAssignment(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePrintDestinationLabel}
                className="flex items-center gap-2"
              >
                🖨️ Generate & Print Label
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Dealer List Modal */}
      {showDealerListModal && (
        <Modal
          isOpen={showDealerListModal}
          onClose={() => setShowDealerListModal(false)}
          title="Manage Dealers"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                📋 Here are all your dealers. Click the edit button (✏️) to update dealer information.
              </p>
            </div>

            {dealers.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <p className="text-gray-600">No dealers found</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  {dealers.map((dealer) => (
                    <div key={dealer.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{dealer.name}</h4>
                            <p className="text-sm text-gray-500">{dealer.mobileNumber}</p>
                            {dealer.businessName && (
                              <p className="text-xs text-gray-400">{dealer.businessName}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge {...mapStatusToBadge(dealer.status)} size="sm" />
                            <button
                              type="button"
                              onClick={() => {
                                handleEditDealer(dealer);
                                setShowDealerListModal(false);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Edit Dealer"
                              aria-label={`Edit dealer ${dealer.name}`}
                            >
                              ✏️
                            </button>
                          </div>
                        </div>
                        {dealer.address && (
                          <p className="text-xs text-gray-500 mt-1">{dealer.address}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setShowDealerListModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

